import React, { useState, useEffect } from "react";
import {
  message,
  Alert,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Modal,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { DynamicTable } from "dynamic-table-builder";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import Heading from "../../components/Heading";
import API from "../../api";
import { APIs } from "../../utils/worker";
import "./style.scss";

class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Alert
          type="error"
          message={this.state.error?.toString()}
          description={<pre style={{ whiteSpace: "pre-wrap" }}>{this.state.error?.stack}</pre>}
        />
      );
    }
    return this.props.children;
  }
}

// Register plugins once for the entire app.
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);

const baseUrl = APIs.delcaperBaseURLv2 || "https://apis2.delcaper.com";
const DA_ATTENDANCE_REPORT_NEW = `${baseUrl}/attendance-service/attendance/report/v1`;
const DA_ATTENDANCE_MATRIX_EXPORT = `${baseUrl}/attendance-service/attendance/report/matrix`;
const { RangePicker, MonthPicker } = DatePicker;

// CRITICAL FIX: The DynamicTable crashes silently if `sorter` and `filter` are missing!
const columns = [
  { title: "Inno Fulfill ID", dataIndex: "innoId", key: "innoId", sorter: false, filter: { type: "search", placeholder: "Search Inno Fulfill ID..." } },
  { title: "DA Name", dataIndex: "daName", key: "daName", sorter: false, filter: false },
  { title: "Date", dataIndex: "date", key: "date", sorter: false, filter: false },
  { title: "DA Mobile", dataIndex: "mobile", key: "mobile", sorter: false, filter: false },
  { title: "Hotspot Name", dataIndex: "hotspotName", key: "hotspotName", sorter: false, filter: false },
  { title: "Total Working Hours", dataIndex: "totalWorkingHours", key: "totalWorkingHours", sorter: false, filter: false },
  { title: "Total Distance Covered(KM)", dataIndex: "distanceCovered", key: "distanceCovered", sorter: false, filter: false },
  { title: "Trip Distance(KM)", dataIndex: "tripDistance", key: "tripDistance", sorter: false, filter: false },
  { title: "Total Deliveries", dataIndex: "totalDeliveries", key: "totalDeliveries", sorter: false, filter: false },
  { title: "Clock In", dataIndex: "clockIn", key: "clockIn", sorter: false, filter: false },
  { title: "Clock Out", dataIndex: "clockOut", key: "clockOut", sorter: false, filter: false },
  { title: "Device ID", dataIndex: "deviceId", key: "deviceId", sorter: false, filter: false },
];

const filters = [];

const EXPORT_FILTER_TYPES = {

  DATE_RANGE: "dateRange",
  MONTH: "month",
  QUARTER: "quarter",
};

const QUARTER_OPTIONS = [
  { label: "Q1 (Jan-Mar)", value: 1 },
  { label: "Q2 (Apr-Jun)", value: 2 },
  { label: "Q3 (Jul-Sep)", value: 3 },
  { label: "Q4 (Oct-Dec)", value: 4 },
];

const DAAttendanceNew = () => {
  const [authError, setAuthError] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [tableConfig, setTableConfig] = useState(null);

  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportFilterType, setExportFilterType] = useState(EXPORT_FILTER_TYPES.DATE_RANGE);
  const [exportDateRange, setExportDateRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [exportMonth, setExportMonth] = useState(dayjs());
  const [exportQuarter, setExportQuarter] = useState(1);
  const [exportYear, setExportYear] = useState(dayjs().year());
  const [isExporting, setIsExporting] = useState(false);

  // Matrix export states
  const [isMatrixModalVisible, setIsMatrixModalVisible] = useState(false);
  const [matrixDateRange, setMatrixDateRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [isMatrixExporting, setIsMatrixExporting] = useState(false);

  const handleAuthError = async () => {
    setAuthError(true);
    message.error("Authentication failed. Please check your token or login again.");
  };

  const handleDataFetch = async (params) => {
    try {
      setApiError(null);

      if (params?.filters?.dateRange && params.filters.dateRange.length === 2) {
        const newDateRange = [
          dayjs(params.filters.dateRange[0]),
          dayjs(params.filters.dateRange[1]),
        ];
        setDateRange(newDateRange);
        if (exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE) {
          setExportDateRange(newDateRange);
        }
      }

      const startDate = params?.filters?.dateRange?.[0]
        ? dayjs(params.filters.dateRange[0]).format("YYYY-MM-DD")
        : dateRange[0].format("YYYY-MM-DD");
      const endDate = params?.filters?.dateRange?.[1]
        ? dayjs(params.filters.dateRange[1]).format("YYYY-MM-DD")
        : dateRange[1].format("YYYY-MM-DD");

      const requestParams = {
        startDate,
        endDate,
        limit: String(params?.pagination?.pageSize || 10),
        page: String(params?.pagination?.pageNumber || 1),
      };

      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value && key !== "dateRange" && key !== "status") {
            requestParams[key] = String(value);
          }
        });
      }

      const response = await API.Api(DA_ATTENDANCE_REPORT_NEW).NodeGetWithPremise(requestParams);

      if (!response.status) {
        if (response.response?.status === 403) {
          await handleAuthError();
          return { data: [], total: 0 };
        }
        const result = response.response?.data;
        if (response.response?.status === 400) {
          const errorMessages = Array.isArray(result?.message) ? result.message : [result?.message || "Validation error"];
          setApiError({ type: "validation", title: result?.error || "Validation Error", messages: errorMessages, statusCode: 400 });
          return { data: [], total: 0 };
        }
        throw new Error(result?.message || "Failed to fetch data");
      }

      const result = response.response?.data;

      if (result && (result.status === 200 || result.status === "success")) {
        const dataWithKeys = Array.isArray(result.data)
          ? result.data.map((item) => ({ ...item, key: item.id || Math.random().toString() }))
          : [];

        const newConfig = {
          ...result,
          status: "success",
          columns: columns,
          filters: filters,
          tableConfig: {
            title: "Delivery Associate Attendance",
            exportFileName: "DA_Attendance_Report.csv",
            globalFilters: [
              { type: "dateRange", key: "dateRange", label: "Date Range" }
            ],
            actionButtons: [
              { key: "export", title: "Export", type: "primary", position: "right", enabled: true }
            ]
          },
          data: dataWithKeys,
          pagination: {
            ...result.pagination,
            current: parseInt(requestParams.page, 10),
            pageSize: parseInt(requestParams.limit, 10),
            total: result.pagination?.total || 0,
            totalCount: result.pagination?.total || 0,
            pageSizeOptions: ["50", "100", "150", "200"],
            showSizeChanger: true,
          },
        };
        setTableConfig(newConfig);

        return {
          data: dataWithKeys,
          total: result.pagination?.total || 0,
          pagination: { ...newConfig.pagination, totalCount: result.pagination?.total || 0 },
        };
      } else {
        throw new Error(result?.message || "Failed to fetch data");
      }
    } catch (error) {
      if (error.message.includes("403")) {
        await handleAuthError();
      } else {
        const errorMessage = error.message || "An unexpected error occurred";
        setApiError({ type: "error", title: "Error", messages: [errorMessage], statusCode: 500 });
      }
      return { data: [], total: 0 };
    }
  };

  useEffect(() => {
    handleDataFetch({
      pagination: { pageNumber: 1, pageSize: 50 },
      filters: { dateRange },
    });
    // eslint-disable-next-line
  }, []);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setApiError(null);
      const exportParams = { export: 1 };

      if (exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE) {
        exportParams.startDate = exportDateRange[0]?.format("YYYY-MM-DD") || null;
        exportParams.endDate = exportDateRange[1]?.format("YYYY-MM-DD") || null;
      } else if (exportFilterType === EXPORT_FILTER_TYPES.MONTH) {
        exportParams.startDate = exportMonth.startOf("month").format("YYYY-MM-DD");
        exportParams.endDate = exportMonth.endOf("month").format("YYYY-MM-DD");
      } else if (exportFilterType === EXPORT_FILTER_TYPES.QUARTER) {
        const start = dayjs().year(exportYear).month((exportQuarter - 1) * 3).startOf("month");
        exportParams.startDate = start.format("YYYY-MM-DD");
        exportParams.endDate = start.add(2, "month").endOf("month").format("YYYY-MM-DD");
      } else {
        exportParams.startDate = dayjs().format("YYYY-MM-DD");
        exportParams.endDate = dayjs().format("YYYY-MM-DD");
      }

      const queryParams = new URLSearchParams();
      Object.entries(exportParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          queryParams.append(key, String(value));
        }
      });

      const response = await API.Api(`${DA_ATTENDANCE_REPORT_NEW}?${queryParams.toString()}`).get();

      if (!response.status) {
        throw new Error(response.response?.data?.message || "Export failed");
      }

      const blob = new Blob([response.response.data], { type: "text/csv" });
      if (blob.size === 0) throw new Error("Received empty file");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileName = `DA_Attendance_New_${dayjs().format("YYYY-MM-DD")}.csv`;
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Export completed successfully");
      setIsExportModalVisible(false);
    } catch (error) {
      let errorMessage = error.message || "An unexpected error occurred during export";
      message.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const handleTableExport = async (params) => {
    if (params?.filters?.dateRange && params.filters.dateRange.length === 2) {
      const newDateRange = [
        dayjs(params.filters.dateRange[0]),
        dayjs(params.filters.dateRange[1]),
      ];
      if (exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE) {
        setExportDateRange(newDateRange);
      }
    }
    setIsExportModalVisible(true);
    return;
  };

  const handleMatrixExport = async () => {
    try {
      setIsMatrixExporting(true);
      setApiError(null);

      const startDate = matrixDateRange[0]?.format("YYYY-MM-DD");
      const endDate = matrixDateRange[1]?.format("YYYY-MM-DD");

      if (!startDate || !endDate) {
        message.error("Please select a valid date range.");
        return;
      }

      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        export: "1",
      });

      // API returns CSV text — use plain .get() and save as .csv
      const response = await API.Api(
        `${DA_ATTENDANCE_MATRIX_EXPORT}?${queryParams.toString()}`
      ).get();

      if (!response.status) {
        throw new Error(response.response?.data?.message || "Matrix export failed");
      }

      const csvData = response.response?.data;
      if (!csvData) throw new Error("Received empty response");

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      if (blob.size === 0) throw new Error("Received empty file");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileName = `DA_Attendance_Matrix_${startDate}_to_${endDate}.csv`;
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Matrix report downloaded successfully");
      setIsMatrixModalVisible(false);
    } catch (error) {
      message.error(error.message || "An unexpected error occurred during matrix export");
    } finally {
      setIsMatrixExporting(false);
    }
  };

  const handleCloseExportModal = () => {
    setIsExportModalVisible(false);
  };

  const renderError = () => {
    if (!apiError && !authError) return null;
    const error = authError
      ? { type: "error", title: "Authentication Error", messages: ["Authentication failed. Please login again."], statusCode: 403 }
      : apiError;

    return (
      <Alert
        message={error.title}
        description={
          <div>
            {error.messages.map((msg, index) => (
              <div key={index} style={{ color: error.type === "error" ? "#ff4d4f" : "#faad14" }}>• {msg}</div>
            ))}
          </div>
        }
        type={error.type === "validation" ? "warning" : "error"}
        showIcon
        style={{ marginBottom: "16px" }}
        closable
        onClose={() => { authError ? setAuthError(false) : setApiError(null); }}
      />
    );
  };

  const renderExportModal = () => {
    return (
      <Modal
        title="Export Attendance Data"
        open={isExportModalVisible}
        onCancel={handleCloseExportModal}
        className="da-attendance-export-modal"
        footer={[
          <Button key="cancel" onClick={handleCloseExportModal}>Cancel</Button>,
          <Button key="export" type="primary" icon={<DownloadOutlined />} loading={isExporting} onClick={handleExport}>
            Export
          </Button>,
        ]}
        width={520}
      >
        <div className="modal-content">
          <Row gutter={[12, 16]}>
            <Col span={24}>
              <div>
                <label className="export-form-label">Select Export Filter Type:</label>
                <Select
                  value={exportFilterType}
                  onChange={setExportFilterType}
                  style={{ width: "100%" }}
                  size="middle"
                  options={[
                    { label: "Date Range", value: EXPORT_FILTER_TYPES.DATE_RANGE },
                    { label: "Month", value: EXPORT_FILTER_TYPES.MONTH },
                    { label: "Quarter", value: EXPORT_FILTER_TYPES.QUARTER },
                  ]}
                />
              </div>
            </Col>
            {exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE && (
              <Col span={24}>
                <div>
                  <label className="export-form-label">Select Date Range:</label>
                  <RangePicker
                    value={exportDateRange}
                    onChange={setExportDateRange}
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    size="middle"
                    placeholder={["Start Date", "End Date"]}
                  />
                </div>
              </Col>
            )}
            {exportFilterType === EXPORT_FILTER_TYPES.MONTH && (
              <Col span={24}>
                <div>
                  <label className="export-form-label">Select Month & Year:</label>
                  <MonthPicker
                    value={exportMonth}
                    onChange={setExportMonth}
                    format="MMMM YYYY"
                    style={{ width: "100%" }}
                    size="middle"
                    placeholder="Select Month"
                  />
                </div>
              </Col>
            )}
            {exportFilterType === EXPORT_FILTER_TYPES.QUARTER && (
              <>
                <Col span={12}>
                  <div>
                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "14px" }}>Select Quarter:</label>
                    <Select
                      value={exportQuarter}
                      onChange={setExportQuarter}
                      style={{ width: "100%" }}
                      size="middle"
                      options={QUARTER_OPTIONS}
                      placeholder="Select Quarter"
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "14px" }}>Select Year:</label>
                    <DatePicker
                      value={dayjs().year(exportYear)}
                      onChange={(date) => setExportYear(date?.year() || dayjs().year())}
                      picker="year"
                      format="YYYY"
                      style={{ width: "100%" }}
                      size="middle"
                      placeholder="Select Year"
                    />
                  </div>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Modal>
    );
  };

  const renderMatrixExportModal = () => (
    <Modal
      title="Download Attendance Matrix Report"
      open={isMatrixModalVisible}
      onCancel={() => setIsMatrixModalVisible(false)}
      className="da-attendance-export-modal"
      footer={[
        <Button key="cancel" onClick={() => setIsMatrixModalVisible(false)}>
          Cancel
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={isMatrixExporting}
          onClick={handleMatrixExport}
        >
          Download Excel
        </Button>,
      ]}
      width={480}
    >
      <div style={{ padding: "8px 0" }}>
        <p style={{ marginBottom: "12px", color: "#555", fontSize: "13px" }}>
          Select a date range to download the attendance matrix report as an Excel file.
        </p>
        <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "14px" }}>
          Date Range:
        </label>
        <RangePicker
          value={matrixDateRange}
          onChange={setMatrixDateRange}
          format="DD-MM-YYYY"
          style={{ width: "100%" }}
          size="middle"
          placeholder={["Start Date", "End Date"]}
        />
      </div>
    </Modal>
  );

  return (
    <div className="p-1">
      <Heading title="Back" />
      <div className="da-attendance-container">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={() => setIsMatrixModalVisible(true)}
            style={{ fontWeight: 600 }}
          >
            Download Matrix Report
          </Button>
        </div>

        {renderError()}
        {tableConfig && (
          <LocalErrorBoundary>
            <DynamicTable
              config={tableConfig}
              onDataFetch={handleDataFetch}
              onExport={handleTableExport}
              defaultFilters={{
                dateRange: {
                  defaultValue: dateRange,
                },
              }}
            />
          </LocalErrorBoundary>
        )}
        {renderExportModal()}
        {renderMatrixExportModal()}
      </div>
    </div>
  );
};

export default DAAttendanceNew;
